import axios from 'axios';
import { api } from '../config';

export interface Initiative {
  id: number;
  use_case_name: string;
  proposed_solution_owner: string;
  solution_sponsor: string;
  purpose: string;
  value: string;
  users: string;
  solution_scope: string;
  proposed_workflow: string;
  inputs_and_data_sources: string;
  submitted_by: string;
  submitted_date: string;
  division?: string;
  status: 'pending' | 'approved' | 'rejected' | 'recalled' | 'parked';
  admin_comment?: string;
  approval_date?: string;
  approved_by?: string;
  notify_approvers?: boolean;
}

export interface User {
  id: number;
  email: string;
  user_type: string;
  is_active: boolean;
  created_at: string;
}

export const initiativeService = {
  submitInitiative: (data: Omit<Initiative, 'id' | 'submitted_date' | 'status'>) => {
    return axios.post(api('/api/initiatives'), data);
  },

  getInitiatives: () => {
    return axios.get<Initiative[]>(api('/api/initiatives'));
  },

  getInitiativeById: (id: number) => {
    return axios.get<Initiative>(api(`/api/initiatives/${id}`));
  },

  getAiDebugLog: () => {
    return axios.get<{ entries: { timestamp: string; source: string; error: string; idea: string }[] }>(api('/api/initiatives/ai-debug-log'));
  },

  runAiCheck: () => {
    return axios.post<{ success: boolean; endpoint: string; deployment: string; api_version: string; response?: string; error?: string }>(api('/api/initiatives/ai-check'));
  },

  approveInitiative: (id: number, comment?: string, approverEmail?: string, notify?: boolean, notifyApprovers?: boolean) => {
    return axios.put(api(`/api/initiatives/${id}/approve`), { comment, approver_email: approverEmail, notify_submitter: notify !== false, notify_approvers: notifyApprovers !== false });
  },

  rejectInitiative: (id: number, comment: string, approverEmail?: string) => {
    return axios.put(api(`/api/initiatives/${id}/reject`), { comment, approver_email: approverEmail });
  },

  parkInitiative: (id: number, comment: string, approverEmail?: string) => {
    return axios.put(api(`/api/initiatives/${id}/park`), { comment, approver_email: approverEmail });
  },

  recallInitiative: (id: number) => {
    return axios.put(api(`/api/initiatives/${id}/recall`), {});
  },

  updateInitiative: (id: number, data: Partial<Initiative>) => {
    return axios.put(api(`/api/initiatives/${id}`), data);
  },

  resubmitInitiative: (id: number, data: Partial<Initiative>) => {
    return axios.put(api(`/api/initiatives/${id}/resubmit`), data);
  },

  deleteInitiative: (id: number) => {
    return axios.delete(api(`/api/initiatives/${id}`));
  },
};

export const notificationService = {
  getNotifications: () => {
    return axios.get(api('/api/notifications'));
  },

  markAsRead: (id: number) => {
    return axios.put(api(`/api/notifications/${id}/read`), {});
  },
};

// Backend keep-alive service
export const backendService = {
  wakeUp: () => {
    return axios.get(api('/wake-up')).catch(() => {});
  },
  health: () => {
    return axios.get(api('/health'));
  },
};

export const userService = {
  getDivision: (email: string) => {
    return axios.get<{ email: string; division: string }>(api(`/api/users/me/${encodeURIComponent(email.toLowerCase())}/division`));
  },
  setDivision: (email: string, division: string) => {
    return axios.put<{ email: string; division: string }>(api(`/api/users/me/${encodeURIComponent(email.toLowerCase())}/division`), null, {
      params: { division },
    });
  },
};
